'use strict'

import { useMemo, useRef, useState } from 'react'
import {
  Badge,
  CircularProgress,
  Collapse,
  Menu,
  MenuItem,
  Popover,
  Switch,
  TextField,
  Tooltip,
  useMediaQuery
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import RefreshIcon from '@material-ui/icons/Refresh'
import FilterListIcon from '@material-ui/icons/FilterList'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import SkipNextIcon from '@material-ui/icons/SkipNext'
import SkipPreviousIcon from '@material-ui/icons/SkipPrevious'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import VisibilityIcon from '@material-ui/icons/Visibility'
import { getCrewColor, getRouteColor } from './crewColors'
import { parseYmd, toYmd, addDays, getWeekTypeForDate } from './scheduleUtils'

const PLANNING_MODES = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'two-week', label: '2-Wk' }
]

const getLegendCode = (name = '', id = '') => {
  const text = String(name || id || '').trim()
  const numberMatch = text.match(/(\d+)(?!.*\d)/)
  if (numberMatch) {
    const number = numberMatch[1]
    if (/^maintenance\b/i.test(text)) return number.padStart(2, '0').slice(-2)
    const initial = (text.match(/[A-Za-z]/)?.[0] || '').toUpperCase()
    return number.length > 1 ? number.slice(-2) : `${initial}${number}`
  }

  const compact = text.replace(/[^a-z0-9]/gi, '').toUpperCase()
  return compact.slice(0, 2) || '?'
}

const getSwatchTextColor = (hex = '') => {
  const match = String(hex).match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i)
  if (!match) return '#fff'
  const [, r, g, b] = match.map(value => parseInt(value, 16))
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.58 ? '#1c2419' : '#fff'
}

/**
 * Floating glass-pill toolbar positioned over the map.
 * Group 1: Route selector + date nav + Day/Week/2-Wk segmented control
 * Group 2: VIP + Unassigned + Legend toggles + filter popover + refresh
 */
export default function MapToolbar ({
  isRouteMode, isFleetMode,
  focusCrewId, setFocusCrewId,
  focusRouteId, setFocusRouteId,
  focusDate, setFocusDate,
  planningMode, setPlanningMode,
  contextWeekType, setContextWeekType,
  availableRoutes, availableCrews,
  showFleetRoutes, setShowFleetRoutes,
  fleetRoutesLoading,
  showRouteStops, setShowRouteStops,
  showUnassigned, setShowUnassigned,
  fleetTodayOnly, setFleetTodayOnly,
  showIrregularMtc, setShowIrregularMtc,
  routeFilterId, setRouteFilterId,
  resetMapControls,
  filterAnchorEl, setFilterAnchorEl,
  showVipOnly, setShowVipOnly,
  showAnchorsOnly, setShowAnchorsOnly,
  showLockedOnly, setShowLockedOnly,
  showCentroids, setShowCentroids,
  showDueOnly, setShowDueOnly,
  frequencyFocus, setFrequencyFocus,
  activeSecondaryFilterCount,
  fetchMapData, loading,
  loadingMetrics, loadingFocusContextStats,
  loadingFocusedRoute,
  propertyFeatures,
  legendOpen, setLegendOpen,
  routeLegend, unassignedCount = 0, routeIds, crewIds, focusRouteFromChip,
  reviewNavOpen, setReviewNavOpen,
  reviewCombos, filteredReviewCombos,
  reviewComboIdx, setReviewComboIdx,
  reviewNavNext, reviewNavPrev,
  navigateToCombo,
  approvedRouteDays, skipApproved, setSkipApproved,
  selectedScenarioId
}) {
  const anyLoading = loading || loadingMetrics || loadingFocusContextStats || loadingFocusedRoute || fleetRoutesLoading
  const weekType = getWeekTypeForDate(parseYmd(focusDate))
  const legendBtnRef = useRef(null)
  const isNarrow = useMediaQuery('(max-width: 1280px)')
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null)

  const focusOptions = useMemo(() => {
    const options = [{ id: '__ALL__', label: 'All Routes (Fleet)', type: 'fleet' }]
    if (isRouteMode) {
      return options.concat((availableRoutes || []).map(route => ({
        id: String(route.routeId),
        label: route.routeName || String(route.routeId),
        type: 'route',
        route
      })))
    }
    return options.concat((availableCrews || []).map(crew => ({
      id: String(crew.crewId),
      label: crew.crewName || String(crew.crewId),
      type: 'crew',
      crew
    })))
  }, [availableCrews, availableRoutes, isRouteMode])

  const selectedFocusOption = useMemo(() => {
    const selectedId = isRouteMode
      ? String(focusRouteId || '')
      : (focusRouteId === '__ALL__' ? '__ALL__' : String(focusCrewId || ''))
    return focusOptions.find(option => option.id === selectedId) || null
  }, [focusCrewId, focusOptions, focusRouteId, isRouteMode])

  const handleRouteChange = (option) => {
    const next = String(option?.id || '')
    if (next === '__ALL__') {
      setFocusRouteId('__ALL__')
      setFocusCrewId('')
      return
    }
    if (isRouteMode) {
      setFocusRouteId(next)
      const route = availableRoutes.find(r => r.routeId === next)
      setFocusCrewId(route?.crewId || '')
    } else {
      setFocusRouteId('')
      setFocusCrewId(next)
    }
  }

  const navigateDate = (direction) => {
    const cur = parseYmd(focusDate)
    const day = cur.getDay()
    let offset
    if (direction === 'prev') {
      offset = day === 1 ? -3 : day === 0 ? -2 : -1
    } else {
      offset = day === 5 ? 3 : day === 6 ? 2 : 1
    }
    setFocusDate(toYmd(addDays(cur, offset)))
  }

  return (
    <>
      {/* ── Toolbar (in-flow above map) ──────────────────────── */}
      <div style={{
        display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-start',
        padding: '10px 16px', flexWrap: 'wrap',
        background: 'var(--df-surface, #fbf8f1)',
        borderBottom: '1px solid var(--df-line, #e0d8c2)'
      }}>
        {/* Group 1: Route + Date + Mode */}
        <div className="toolbar-pill">
          <Autocomplete
            size="small"
            options={focusOptions}
            value={selectedFocusOption}
            getOptionLabel={(option) => option?.label || ''}
            getOptionSelected={(option, value) => option?.id === value?.id}
            onChange={(_event, option) => handleRouteChange(option)}
            style={{ width: isNarrow ? 320 : 460, flexShrink: 0 }}
            renderOption={(option) => {
              const color = option.type === 'route'
                ? getRouteColor(option.id, routeIds)
                : option.type === 'crew'
                  ? getCrewColor(option.id, crewIds)
                  : 'var(--df-primary, #1f5e3a)'
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: color
                  }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {option.label}
                  </span>
                </div>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={`Focus ${isRouteMode ? 'route' : 'crew'}`}
                InputProps={{
                  ...params.InputProps,
                  style: {
                    ...params.InputProps.style,
                    height: 30,
                    minHeight: 30,
                    paddingTop: 0,
                    paddingBottom: 0,
                    borderRadius: 5,
                    fontSize: 12,
                    fontFamily: 'inherit',
                    color: 'var(--df-ink-2, #3d463a)',
                    background: 'transparent'
                  }
                }}
              />
            )}
          />

          {isFleetMode && (
            <span style={{
              padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700,
              background: 'var(--df-primary-soft, #e3eee0)', color: 'var(--df-primary, #1f5e3a)',
              whiteSpace: 'nowrap'
            }}>FLEET</span>
          )}

          <div className="toolbar-divider" />

          <Tooltip title="Previous business day" arrow>
            <button
              className="toolbar-icon-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 5, border: 'none',
                background: 'transparent', cursor: 'pointer',
                color: 'var(--df-ink-4, #97a094)'
              }}
              onClick={() => navigateDate('prev')}
            >
              <ChevronLeftIcon style={{ fontSize: 18 }} />
            </button>
          </Tooltip>

          <input
            type="date"
            value={focusDate}
            onChange={(e) => setFocusDate(String(e.target.value || toYmd(new Date())))}
            style={{
              padding: '4px 8px', borderRadius: 5,
              border: '1px solid var(--df-line, #e0d8c2)',
              fontSize: 12, fontFamily: 'inherit', outline: 'none',
              color: 'var(--df-ink-2, #3d463a)', background: 'transparent'
            }}
          />

          <Tooltip title="Next business day" arrow>
            <button
              className="toolbar-icon-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 5, border: 'none',
                background: 'transparent', cursor: 'pointer',
                color: 'var(--df-ink-4, #97a094)'
              }}
              onClick={() => navigateDate('next')}
            >
              <ChevronRightIcon style={{ fontSize: 18 }} />
            </button>
          </Tooltip>

          {(() => {
            const todayYmd = toYmd(new Date())
            const isToday = focusDate === todayYmd
            return (
              <Tooltip title={isToday ? 'Already on today' : 'Jump to today'} arrow>
                <span>
                  <button
                    className="toolbar-btn"
                    disabled={isToday}
                    onClick={() => setFocusDate(todayYmd)}
                    style={{
                      fontSize: 10.5, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
                      background: isToday ? 'transparent' : 'var(--df-primary-soft, #e3eee0)',
                      color: isToday ? 'var(--df-ink-4, #97a094)' : 'var(--df-primary, #1f5e3a)',
                      border: 'none', cursor: isToday ? 'default' : 'pointer',
                      opacity: isToday ? 0.5 : 1
                    }}
                  >
                    Today
                  </button>
                </span>
              </Tooltip>
            )
          })()}

          <span style={{
            fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
            background: weekType === 'A' ? '#dde9f5' : '#f5dde2',
            color: weekType === 'A' ? '#1565c0' : '#c62828',
            whiteSpace: 'nowrap'
          }}>Wk {weekType}</span>

          <div className="toolbar-divider" />

          {/* Segmented control: Day / Week / 2-Wk */}
          <div className="seg-control" style={{ padding: 2 }}>
            {PLANNING_MODES.map(m => (
              <button
                key={m.value}
                className={'seg-btn' + (planningMode === m.value ? ' active' : '')}
                onClick={() => {
                  setPlanningMode(m.value)
                  if (m.value === 'two-week') setContextWeekType('ALL')
                }}
                style={{ padding: '3px 9px', fontSize: 11 }}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Group 2: Toggles + filters */}
        <div className="toolbar-pill">
          {isFleetMode && (
            <button
              className={'toolbar-btn' + (showFleetRoutes ? ' active' : '')}
              style={{
                fontSize: 10.5, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
                background: showFleetRoutes ? 'var(--df-ok, #2f7a3e)' : 'transparent',
                color: showFleetRoutes ? '#fff' : 'var(--df-ink-3)', border: 'none', cursor: 'pointer'
              }}
              onClick={() => setShowFleetRoutes(prev => !prev)}
            >
              FLEET VIEW
            </button>
          )}

          {!isNarrow && (
            <>
              <button
                className={'toolbar-btn' + (fleetTodayOnly ? ' active' : '')}
                style={{
                  fontSize: 10.5, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
                  background: fleetTodayOnly ? 'var(--df-info, #3454c1)' : 'transparent',
                  color: fleetTodayOnly ? '#fff' : 'var(--df-ink-3)', border: 'none', cursor: 'pointer'
                }}
                onClick={() => setFleetTodayOnly(prev => !prev)}
              >
                Today&apos;s Stops
              </button>

              <button
                className={'toolbar-btn' + (showRouteStops ? ' active' : '')}
                style={{
                  fontSize: 10.5, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
                  background: showRouteStops ? 'var(--df-modify, #b35d1a)' : 'transparent',
                  color: showRouteStops ? '#fff' : 'var(--df-ink-3)', border: 'none', cursor: 'pointer'
                }}
                onClick={() => setShowRouteStops(prev => !prev)}
              >
                Routes ON
              </button>

              <button
                className={'toolbar-btn' + (showUnassigned ? ' active' : '')}
                style={{
                  fontSize: 10.5, fontWeight: 700, padding: '4px 8px 4px 10px', borderRadius: 6,
                  background: showUnassigned ? 'var(--df-ink-4, #97a094)' : 'transparent',
                  color: showUnassigned ? '#fff' : 'var(--df-ink-3)', border: 'none', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6
                }}
                onClick={() => {
                  if (routeFilterId === 'unassigned') setRouteFilterId('')
                  setShowUnassigned(prev => !prev)
                }}
              >
                <span>Unassigned</span>
                <span style={{
                  minWidth: 18, height: 16, borderRadius: 99, padding: '0 5px',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: Number(unassignedCount) > 0 ? 'var(--df-danger, #a9341d)' : 'var(--df-ok, #2f7a3e)',
                  color: '#fff', fontSize: 10, fontWeight: 800, lineHeight: 1
                }}>
                  {unassignedCount}
                </span>
              </button>

              <button
                className={'toolbar-btn' + (showIrregularMtc ? ' active' : '')}
                style={{
                  fontSize: 10.5, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
                  background: showIrregularMtc ? 'var(--df-warn, #8c5b00)' : 'transparent',
                  color: showIrregularMtc ? '#fff' : 'var(--df-ink-3)', border: 'none', cursor: 'pointer'
                }}
                onClick={() => setShowIrregularMtc(prev => !prev)}
              >
                Irregular Mtc
              </button>
            </>
          )}

          {isNarrow && (() => {
            const activeCount = (fleetTodayOnly ? 1 : 0) + (showRouteStops ? 1 : 0) + (showUnassigned ? 1 : 0) + (showIrregularMtc ? 1 : 0)
            return (
              <>
                <Tooltip title="More toggles" arrow>
                  <button
                    className={'toolbar-icon-btn' + (activeCount > 0 ? ' active' : '')}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 30, height: 30, borderRadius: 6, border: 'none',
                      cursor: 'pointer', transition: 'all 0.12s',
                      background: activeCount > 0 ? 'var(--df-primary-soft, #e3eee0)' : 'transparent',
                      color: activeCount > 0 ? 'var(--df-primary, #1f5e3a)' : 'var(--df-ink-4, #97a094)'
                    }}
                    onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                  >
                    <Badge badgeContent={activeCount} color="secondary" overlap="rectangular">
                      <MoreHorizIcon style={{ fontSize: 18 }} />
                    </Badge>
                  </button>
                </Tooltip>
                <Menu
                  anchorEl={moreMenuAnchor}
                  open={Boolean(moreMenuAnchor)}
                  onClose={() => setMoreMenuAnchor(null)}
                  getContentAnchorEl={null}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <MenuItem onClick={() => setFleetTodayOnly(prev => !prev)} dense>
                    <Switch checked={fleetTodayOnly} size="small" /> Today&apos;s Stops
                  </MenuItem>
                  <MenuItem onClick={() => setShowRouteStops(prev => !prev)} dense>
                    <Switch checked={showRouteStops} size="small" /> Routes ON
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      if (routeFilterId === 'unassigned') setRouteFilterId('')
                      setShowUnassigned(prev => !prev)
                    }}
                    dense
                  >
                    <Switch checked={showUnassigned} size="small" /> Unassigned ({unassignedCount})
                  </MenuItem>
                  <MenuItem onClick={() => setShowIrregularMtc(prev => !prev)} dense>
                    <Switch checked={showIrregularMtc} size="small" /> Irregular Mtc
                  </MenuItem>
                </Menu>
              </>
            )
          })()}

          <div style={{ width: 1, height: 20, background: 'var(--df-line, #e0d8c2)', margin: '0 2px', flexShrink: 0 }} />

          <Tooltip title={legendOpen ? 'Hide legend' : 'Show legend'} arrow>
            <button
              ref={legendBtnRef}
              className={'toolbar-icon-btn' + (legendOpen ? ' active' : '')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: 6, border: 'none',
                cursor: 'pointer', transition: 'all 0.12s',
                background: legendOpen ? 'var(--df-primary-soft, #e3eee0)' : 'transparent',
                color: legendOpen ? 'var(--df-primary, #1f5e3a)' : 'var(--df-ink-4, #97a094)'
              }}
              onClick={() => setLegendOpen(prev => !prev)}
            >
              <VisibilityIcon style={{ fontSize: 18 }} />
            </button>
          </Tooltip>

          <Tooltip title={`${propertyFeatures.length} props visible — click to filter`} arrow>
            <button
              className="toolbar-icon-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: 6, border: 'none',
                cursor: 'pointer', transition: 'all 0.12s',
                background: activeSecondaryFilterCount > 0 ? 'var(--df-primary-soft, #e3eee0)' : 'transparent',
                color: activeSecondaryFilterCount > 0 ? 'var(--df-primary, #1f5e3a)' : 'var(--df-ink-4, #97a094)',
                position: 'relative'
              }}
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            >
              <Badge badgeContent={activeSecondaryFilterCount} color="secondary" overlap="rectangular">
                <FilterListIcon style={{ fontSize: 18 }} />
              </Badge>
            </button>
          </Tooltip>

          <Tooltip title="Refresh map data" arrow>
            <button
              className="toolbar-icon-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: 6, border: 'none',
                cursor: 'pointer', transition: 'all 0.12s',
                background: 'transparent',
                color: 'var(--df-ink-4, #97a094)'
              }}
              onClick={fetchMapData}
              disabled={loading}
            >
              {anyLoading
                ? <CircularProgress size={16} style={{ color: 'var(--df-primary, #1f5e3a)' }} />
                : <RefreshIcon style={{ fontSize: 18 }} />
              }
            </button>
          </Tooltip>

        </div>

        {/* Group 3: Review button (only when scenario + combos exist) */}
        {!!selectedScenarioId && reviewCombos.length > 0 && (
          <div className="toolbar-pill">
            <Tooltip title={reviewNavOpen ? 'Close review navigator' : 'Step through each combo to approve'} arrow>
              <button
                className={'toolbar-icon-btn' + (reviewNavOpen ? ' active' : '')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  width: 'auto', padding: '4px 10px', borderRadius: 6, border: 'none',
                  cursor: 'pointer', transition: 'all 0.12s', fontFamily: 'inherit',
                  fontSize: 11.5, fontWeight: 600,
                  background: reviewNavOpen ? 'var(--df-primary, #1f5e3a)' : 'transparent',
                  color: reviewNavOpen ? '#fff' : 'var(--df-ink-4, #97a094)'
                }}
                onClick={() => {
                  setReviewNavOpen(prev => !prev)
                  if (!reviewNavOpen && filteredReviewCombos.length) {
                    navigateToCombo(filteredReviewCombos[reviewComboIdx])
                  }
                }}
              >
                <CheckCircleIcon style={{ fontSize: 16 }} />
                Review ({approvedRouteDays.length}/{reviewCombos.length})
              </button>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Filter popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div style={{
          padding: 16, display: 'flex', flexDirection: 'column', gap: 8,
          minWidth: 220, background: 'var(--df-surface, #fbf8f1)'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
            <Switch checked={showVipOnly} onChange={(e) => setShowVipOnly(e.target.checked)} size="small" />
            VIP only
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
            <Switch checked={showAnchorsOnly} onChange={(e) => setShowAnchorsOnly(e.target.checked)} size="small" />
            Anchors only
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
            <Switch checked={showLockedOnly} onChange={(e) => setShowLockedOnly(e.target.checked)} size="small" />
            Locked only
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
            <Switch checked={showCentroids} onChange={(e) => setShowCentroids(e.target.checked)} size="small" />
            Centroids
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
            <Switch checked={showDueOnly} onChange={(e) => setShowDueOnly(e.target.checked)} size="small" />
            Due in context
          </label>

          {isFleetMode && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
                <Switch checked={fleetTodayOnly} onChange={(e) => setFleetTodayOnly(e.target.checked)} size="small" />
                Today&apos;s stops only
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
                <Switch checked={showFleetRoutes} onChange={(e) => setShowFleetRoutes(e.target.checked)} size="small" />
                Route lines
              </label>
            </>
          )}

          {!isFleetMode && focusCrewId && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
              <Switch checked={showRouteStops} onChange={(e) => setShowRouteStops(e.target.checked)} size="small" />
              Focused route only
            </label>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--df-ink-2, #3d463a)', cursor: 'pointer' }}>
            <Switch checked={showIrregularMtc} onChange={(e) => setShowIrregularMtc(e.target.checked)} size="small" />
            Irregular maintenance
          </label>

          <div style={{ height: 1, background: 'var(--df-line, #e0d8c2)', margin: '4px 0' }} />

          <select
            value={contextWeekType}
            onChange={(e) => setContextWeekType(String(e.target.value || 'ALL'))}
            style={{
              padding: '5px 8px', borderRadius: 5, border: '1px solid var(--df-line, #e0d8c2)',
              fontSize: 12, fontFamily: 'inherit', color: 'var(--df-ink-2, #3d463a)',
              background: 'transparent', cursor: 'pointer', width: '100%'
            }}
          >
            <option value="ALL">Any Week</option>
            <option value="A">Week A</option>
            <option value="B">Week B</option>
          </select>

          <select
            value={frequencyFocus}
            onChange={(e) => setFrequencyFocus(String(e.target.value || 'all'))}
            style={{
              padding: '5px 8px', borderRadius: 5, border: '1px solid var(--df-line, #e0d8c2)',
              fontSize: 12, fontFamily: 'inherit', color: 'var(--df-ink-2, #3d463a)',
              background: 'transparent', cursor: 'pointer', width: '100%'
            }}
          >
            <option value="all">All Freq</option>
            <option value="weekly">Not Bi-Weekly</option>
            <option value="biweekly">Bi-Weekly</option>
          </select>

          <button
            onClick={resetMapControls}
            disabled={activeSecondaryFilterCount === 0}
            style={{
              padding: '6px 10px', borderRadius: 6, border: '1px solid var(--df-line, #e0d8c2)',
              background: 'transparent', cursor: activeSecondaryFilterCount === 0 ? 'not-allowed' : 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              color: activeSecondaryFilterCount === 0 ? 'var(--df-ink-4, #97a094)' : 'var(--df-ink-2, #3d463a)',
              opacity: activeSecondaryFilterCount === 0 ? 0.5 : 1
            }}
          >
            Reset Filters
          </button>
        </div>
      </Popover>

      {/* ── Review navigator bar (below toolbar, floating) ─── */}
      <Collapse in={reviewNavOpen && filteredReviewCombos.length > 0}>
        <div style={{
          position: 'absolute', top: 56, left: '50%', transform: 'translateX(-50%)',
          zIndex: 110, display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 12px', borderRadius: 'var(--df-radius, 10px)',
          background: 'rgba(251,248,241,0.96)', backdropFilter: 'blur(10px)',
          border: '1px solid var(--df-line-2, #d1c8ac)',
          boxShadow: '0 4px 12px rgba(28,36,25,0.10)'
        }}>
          <Tooltip title="Previous combo" arrow>
            <button
              className="toolbar-icon-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 5, border: 'none',
                background: 'transparent', cursor: 'pointer',
                color: 'var(--df-ink-4, #97a094)'
              }}
              onClick={reviewNavPrev}
              disabled={filteredReviewCombos.length <= 1}
            >
              <SkipPreviousIcon style={{ fontSize: 16 }} />
            </button>
          </Tooltip>

          {(() => {
            const combo = filteredReviewCombos[reviewComboIdx]
            if (!combo) return null
            const wtColor = combo.weekType === 'A' ? '#1565c0' : '#c62828'
            const wtBg = combo.weekType === 'A' ? '#dde9f5' : '#f5dde2'
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {combo.approved
                  ? <CheckCircleIcon style={{ color: '#2f7a3e', fontSize: 16 }} />
                  : <RadioButtonUncheckedIcon style={{ color: 'var(--df-ink-4, #97a094)', fontSize: 16 }} />
                }
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--df-ink, #1c2419)', whiteSpace: 'nowrap' }}>
                  {combo.routeName || combo.crewId}
                </span>
                <span style={{
                  fontSize: 10.5, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                  background: 'var(--df-bg-2, #ece5d3)', color: 'var(--df-ink-3, #6f7a6b)'
                }}>{combo.dayName.slice(0, 3)}</span>
                <span style={{
                  fontSize: 10.5, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                  background: wtBg, color: wtColor
                }}>Wk {combo.weekType}</span>
              </div>
            )
          })()}

          <Tooltip title="Next combo" arrow>
            <button
              className="toolbar-icon-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 5, border: 'none',
                background: 'transparent', cursor: 'pointer',
                color: 'var(--df-ink-4, #97a094)'
              }}
              onClick={reviewNavNext}
              disabled={filteredReviewCombos.length <= 1}
            >
              <SkipNextIcon style={{ fontSize: 16 }} />
            </button>
          </Tooltip>

          <div style={{ width: 1, height: 16, background: 'var(--df-line, #e0d8c2)', flexShrink: 0 }} />

          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--df-ink-3, #6f7a6b)', whiteSpace: 'nowrap' }}>
            {reviewComboIdx + 1} / {filteredReviewCombos.length}
          </span>

          <button
            onClick={() => { setSkipApproved(prev => !prev); setReviewComboIdx(0) }}
            style={{
              padding: '3px 8px', borderRadius: 5, border: 'none',
              cursor: 'pointer', fontSize: 10.5, fontWeight: 600, fontFamily: 'inherit',
              background: skipApproved ? 'var(--df-primary, #1f5e3a)' : 'var(--df-bg-2, #ece5d3)',
              color: skipApproved ? '#fff' : 'var(--df-ink-3, #6f7a6b)',
              transition: 'all 0.12s'
            }}
          >
            {skipApproved ? 'Unapproved only' : 'Show all'}
          </button>

          <span style={{ fontSize: 11, fontWeight: 600, color: '#2f7a3e', whiteSpace: 'nowrap' }}>
            {approvedRouteDays.length} approved
          </span>
        </div>
      </Collapse>

      {/* ── Legend popover (anchored to Legend toggle button) ────────── */}
      <Popover
        open={legendOpen && Boolean(legendBtnRef.current)}
        anchorEl={legendBtnRef.current}
        onClose={() => setLegendOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          style: {
            marginTop: 6,
            background: 'var(--df-surface, #fbf8f1)',
            border: '1px solid var(--df-line, #e0d8c2)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(28,36,25,0.14)'
          }
        }}
      >
        <div style={{
          padding: '12px 14px',
          display: 'flex', flexDirection: 'column', gap: 8, minWidth: 200, maxHeight: 360, overflowY: 'auto'
        }}>
          {routeLegend.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--df-ink-3, #6f7a6b)', fontStyle: 'italic' }}>
              No routes loaded
            </div>
          )}
          {routeLegend.map(([id, name, count]) => {
            const swatchColor = getRouteColor(id, routeIds) || getCrewColor(id, crewIds)
            return (
              <div
                key={id}
                onClick={() => focusRouteFromChip(id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                  opacity: routeFilterId && String(routeFilterId) !== String(id) ? 0.4 : 1,
                  transition: 'opacity 0.15s'
                }}
              >
                <span style={{
                  width: 24, height: 20, borderRadius: 999, flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: swatchColor,
                  color: getSwatchTextColor(swatchColor),
                  border: '1px solid rgba(255,255,255,0.95)',
                  boxShadow: '0 0 0 1px rgba(28,36,25,0.22)',
                  fontSize: 9,
                  fontWeight: 800,
                  lineHeight: 1,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                }}>
                  {getLegendCode(name, id)}
                </span>
                <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: 'var(--df-ink-2, #3d463a)' }}>
                  {name}
                </span>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--df-ink-4, #97a094)', marginLeft: 8 }}>
                  {count}
                </span>
              </div>
            )
          })}
          {(() => {
            const assignedCount = routeLegend.reduce((sum, [, , c]) => sum + (c || 0), 0)
            const unassignedCount = Math.max(0, propertyFeatures.length - assignedCount)
            return (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                paddingTop: 6, borderTop: '1px solid var(--df-line, #ede6d2)',
                cursor: 'pointer',
                opacity: routeFilterId && routeFilterId !== 'unassigned' ? 0.4 : 1,
                transition: 'opacity 0.15s'
              }}
                onClick={() => {
                  const next = routeFilterId === 'unassigned' ? '' : 'unassigned'
                  resetMapControls()
                  setRouteFilterId(next)
                }}
              >
                <span style={{
                  width: 24, height: 20, borderRadius: 999, background: '#9E9E9E', flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.95)',
                  boxShadow: '0 0 0 1px rgba(28,36,25,0.22)',
                  fontSize: 9,
                  fontWeight: 800,
                  lineHeight: 1,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                }}>UA</span>
                <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: '#a9341d' }}>Unassigned</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--df-ink-4, #97a094)', marginLeft: 8 }}>
                  {unassignedCount}
                </span>
              </div>
            )
          })()}
        </div>
      </Popover>
    </>
  )
}
